/** @jsxImportSource @emotion/react */
import * as React from 'react';

// eslint-disable-next-line no-unused-vars
import { jsx } from '@emotion/react';
import ReactSelect, { components as defaultComponents } from 'react-select';
import tokens from '@gandi/ds-primitives';
import { useTranslator } from '@gandi/translate';
import { Base } from '@gandi/ui';
import { useReadonly, usePortal } from '../../hooks';
import * as theme from '../theme';
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
const styles = {
  ...theme.styles,
  option: (provided, state) => {
    let backgroundColor;
    if (state.isSelected) {
      backgroundColor = tokens.color.neutral['800'];
    } else if (state.isFocused) {
      backgroundColor = tokens.color.neutral['50'];
    }
    return {
      ...provided,
      borderRadius: tokens.border.radius['250'],
      '&:not(:last-child)': {
        marginBottom: tokens.spacing['1x']
      },
      backgroundColor,
      '& p, & span': {
        color: state.isSelected && 'white'
      },
      '& svg': {
        fill: state.isSelected && 'white'
      },
      ':active': !state.data.isDisabled && {
        backgroundColor: tokens.color.neutral['600'],
        color: 'white',
        '& p, & span': {
          color: 'white'
        },
        '& svg': {
          fill: 'white'
        }
      }
    };
  },
  valueContainer: (provided, state) => {
    if (state.selectProps.isMulti) {
      // $FlowFixMe[prop-missing]
      return theme.styles.valueContainer(provided, state);
    }
    return {
      ...provided,
      padding: false
    };
  },
  placeholder: (provided, state) => {
    if (state.selectProps.isMulti) {
      return provided;
    }
    return {
      ...provided,
      paddingLeft: tokens.spacing['5x']
    };
  },
  singleValue: provided => ({
    ...provided,
    position: false,
    top: false,
    marginLeft: false,
    marginRight: false,
    paddingLeft: tokens.spacing['2x'],
    paddingRight: tokens.spacing['2x'],
    transform: false,
    width: '100%'
  }),
  group: () => ({}),
  groupHeading: (provided, state) => ({
    // $FlowFixMe[prop-missing]
    ...theme.styles.groupHeading(provided, state),
    backgroundColor: tokens.form.widget.surface.color.base,
    paddingLeft: tokens.spacing['4x']
  })
};
function SingleValue(props) {
  const {
    selectProps: {
      menuIsOpen,
      isDisabled,
      isReadonly,
      uiRenderOption: render
    },
    data,
    isSelected
  } = props;
  return _jsx(defaultComponents.SingleValue, {
    ...props,
    children: menuIsOpen ? null : _jsx(Base, {
      children: typeof render === 'function' ? render(data, {
        isSelected,
        asValue: true,
        isDisabled,
        isReadonly
      }) : null
    })
  });
}
function Option(props) {
  const {
    selectProps,
    data,
    isSelected,
    isDisabled
  } = props;
  const {
    uiRenderOption: render
  } = selectProps;
  const optionTheme = (() => {
    if (isDisabled) {
      return {};
    }
    return {
      interactive: true
    };
  })();
  return _jsx(defaultComponents.Option, {
    ...props,
    children: _jsx(Base, {
      ...optionTheme,
      children: typeof render === 'function' ? render(data, {
        isSelected,
        asValue: false
      }) : null
    })
  });
}
const components = {
  ...theme.components,
  SingleValue,
  Option
};
const setValueFirst = (value, options) => {
  if (!value) {
    return options;
  }
  const idx = options.indexOf(value);
  if (idx <= 0) {
    return options;
  }
  const updated = [...options];
  updated.splice(idx, 1);
  // $FlowFixMe[incompatible-call]
  updated.unshift(value);
  return updated;
};
function Custom(props, ref) {
  const {
    renderOption,
    options: optionsProp,
    onChange,
    isReadonly,
    ...extraProps
  } = props;
  const {
    __
  } = useTranslator();
  const readOnlyProps = useReadonly({
    isReadonly
  });
  const portalProps = usePortal();
  const [options, setOptions] = React.useState(() => setValueFirst(props.defaultValue, optionsProp));

  // Update the available options and the default value when their value change.
  React.useEffect(() => {
    setOptions(setValueFirst(props.value || props.defaultValue, optionsProp));
    // WARN: Do not set `props.defaultValue` as a dependency
    // in order to correctly ignore this props change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [optionsProp, props.value]);

  // Memoize the `handleChange` callback.
  const handleChange = React.useCallback((value, params) => {
    setOptions(setValueFirst(value, optionsProp));
    if (typeof onChange === 'function') {
      onChange(value, params);
    }
  }, [optionsProp, setOptions, onChange]);
  return (
    // $FlowFixMe[cannot-spread-inexact]
    _jsx(ReactSelect
    // This will return the component instance `SelectInstance<>`, not the DOM node.
    // Expected to be able to access certain methods, such as `ref.focus()`.
    , {
      ref: ref,
      options: options,
      onChange: handleChange,
      isReadonly: isReadonly,
      uiRenderOption: renderOption,
      theme: theme.theme,
      styles: styles,
      components: components,
      placeholder: __('Select...'),
      ...readOnlyProps,
      ...portalProps,
      ...extraProps
    })
  );
}
const Export = /*#__PURE__*/React.forwardRef(Custom);
Export.displayName = 'Custom';
export { Export as Custom };